import { JSEncrypt } from 'jsencrypt';
import { Site, SLD } from '@patsnap-utils/biz';
import { useLinks } from './use-links';
import { getFreeRegisterUrlParams } from '../utils/get-register-url-params';
import { reactive } from 'vue';

import {
    isPersonalEmail,
    isValidEmail,
    isValidPassword,
} from '../utils/validate';

import { ERROR_CODE } from '../types/error-codes';

import {
    getPublicKeyApi,
    postFreeRegisterApi,
} from '../apis/sign-up';

import { CLOSE, useCaptcha } from './use-captcha';
import { useI18n } from 'vue-i18n';
import { useChannels } from './use-channels';

const EUREKA_CLIENT_NAME = import.meta.env.VITE_PASSPORT_CLIENT_NAME

const EUREKA_CLIENT_ID = import.meta.env.VITE_PASSPORT_CLIENT_ID

const EUREKA_URL = import.meta.env.VITE_EUREKA_URI

const { generateRedirectUri } = useLinks();
/**
 * 注册成功之后，激活邮件里的点击激活链接。
 */
const generateEmailActiveLink = () => {
    const registerParams = getFreeRegisterUrlParams();
    const data = {
        ...registerParams,
        from: EUREKA_CLIENT_NAME,
        redirect_uri: `${EUREKA_URL}${generateRedirectUri()}`,
        client_id: EUREKA_CLIENT_ID,
    };
    const params = new URLSearchParams();
    Object.keys(data).forEach((key) => {
        // @ts-ignore
        params.append(key, data[key]);
    });
    return `${new Site({ sld: SLD.ACCOUNT }).href}/public/?${params.toString()}#/active`;
};

/**
 *  封装一个注册的逻辑, 用于注册用户。其中维护 一个 loading 状态，一个错误信息，一个注册成功的信息，一个注册的方法，一个重置的方法, 一个表单数据,
 */
export function useRegister() {
    const { t } = useI18n();
    const { channels } = useChannels()

    const form = reactive({
        email: '',
        password: '',
        agreed: false,
        loading: false,
    });

    const emailActiveLink = generateEmailActiveLink();

    const errors = reactive({
        email: '',
        password: '',
        server: '',
    });

    let registerOk: any;
    const registerPromise = new Promise((resolve) => {
        registerOk = resolve;
    });

    function validatePassword() {
        if (form.password === '') {
            errors.password = t('us.register_email.password.required');
        } else if (!isValidPassword(form.password)) {
            errors.password = t('us.register_email.password.invalid');
        } else {
            errors.password = '';
        }
        return !errors.password;
    }

    function validateEmail() {
        errors.server = '';
        if (form.email === '') {
            errors.email = t('us.register_email.email.required');
        } else if (!isValidEmail(form.email)) {
            errors.email = t('us.register_email.email.invalid');
        } else if (isPersonalEmail(form.email)) {
            errors.email = t('us.register_email.email.personalEmail');
        } else {
            errors.email = '';
        }
        return !errors.email;
    }

    const { verify } = useCaptcha();

    async function register() {
        const emailValid = validateEmail();
        const passwordValid = validatePassword();
        if (!emailValid || !passwordValid) {
            return;
        }
        form.loading = true;
        try {
            const captchaVerifyResult = await verify();
            if (captchaVerifyResult === CLOSE) {
                form.loading = false;
                return;
            }
            const publicKey = await getPublicKeyApi();
            const encrypt = new JSEncrypt({});
            encrypt.setPublicKey(publicKey);
            const encryptPwd = encrypt.encrypt(form.password);
            await postFreeRegisterApi(
                {
                    ...channels.value,
                    ...getFreeRegisterUrlParams(),
                    email: form.email,
                    password: encryptPwd,
                },
                {
                    login_email_binding_uri: emailActiveLink,
                    product_name: 'eureka',
                    client_id: EUREKA_CLIENT_ID,
                    ...captchaVerifyResult,
                }
            );
        } catch (e) {
            let errorCode = e?.response?.data?.errcode || e?.response?.data?.numeric_error_code;
            const status = e?.response?.status;
            errors.server = t('us.register_email.server.busy');
            if (status === 500) {
                errorCode = -1;
            }
            if (errorCode === ERROR_CODE.EMAIL_OR_MOBILE_EXIST) {
                errors.server = t('us.register_email.email.exists');
            }
            if (errorCode === ERROR_CODE.COMPANY_AND_EMAIL_IN_BLACKLIST) {
                errors.server = t('us.register_email.email.inBlacklist');
            }
            return;
        } finally {
            form.loading = false;
        }
        registerOk();
    }

    function resetForm() {
        form.email = '';
        form.password = '';
        form.agreed = false;
        errors.email = '';
        errors.password = '';
    }

    return {
        form,
        validateEmail,
        validatePassword,
        errors,
        register,
        resetForm,
        registerPromise,
    };
}
